import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command as CommandPrimitive } from 'cmdk'

import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/UI/Dialog'
import { cn } from '@/utils/cn'
import { useBiographies } from '@/hooks/use-biographies'
import { useFileDownloadUrl } from '@/hooks/use-file-download-url'
import { type Biography } from '@/api/biography'
import CoverContainer from '@/components/CoverContainer'
import { debounce } from 'es-toolkit'
import { Spinner } from '../UI'

interface GlobalSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// 单个搜索结果项组件
const SearchResultItem = ({
  biography,
  isSelected,
  onSelect,
}: {
  biography: Biography
  isSelected: boolean
  onSelect: () => void
}) => {
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const { getDownloadUrl } = useFileDownloadUrl()

  useEffect(() => {
    let isCancelled = false

    const loadCover = async () => {
      if (biography.cover_file_key) {
        try {
          const url = await getDownloadUrl(biography.cover_file_key)
          if (!isCancelled && url) {
            setCoverUrl(url)
          }
        } catch (error) {
          console.error('加载封面失败:', error)
        }
      }
    }

    loadCover()

    return () => {
      isCancelled = true
    }
  }, [biography.cover_file_key, getDownloadUrl])

  return (
    <CommandPrimitive.Item
      value={`${biography.id}-${biography.title}-${biography.client?.name || ''}`}
      onSelect={onSelect}
      className={cn(
        'flex items-center gap-3 p-1 rounded-3 cursor-pointer',
        'data-[selected=true]:bg-overlay-1 group'
      )}
    >
      {/* 封面缩略图 */}
      <div className="flex items-start justify-center overflow-hidden size-12 pt-2 bg-surfground bor-1 rounded-2.5 group-hover:border-stroke-2 dark:group-hover:border-stroke-3 transition-border-color duration-300">
        <CoverContainer
          imageUrl={coverUrl}
          sizeClassName="w-33px h-48px"
          className='shadow-sm'
        />
      </div>

      {/* 标题和讲述人 */}
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-body-md text-primary truncate">{biography.title}</span>
        <span className="text-body-md text-tertiary truncate">
          {biography.client?.name || '未知委托人'}
        </span>
      </div>

      {/* Enter 图标（选中时显示） */}
      <div className={cn('shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}>
        <iconpark-icon name="outline-enter" size="18" class='text-brand-primary' />
      </div>
    </CommandPrimitive.Item>
  )
}

const GlobalSearch = ({ open, onOpenChange }: GlobalSearchProps) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [selectedValue, setSelectedValue] = useState('')

  const { biographies, isLoading, fetchBiographies } = useBiographies({
    autoFetch: false,
    initialParams: { page: 1, page_size: 20 },
  })

  // 创建防抖搜索函数
  const debouncedFetch = useCallback(
    debounce((searchQuery: string) => {
      fetchBiographies({ page: 1, page_size: 20, search: searchQuery })
    }, 300),
    []
  )

  // 当搜索词改变时，调用防抖函数
  useEffect(() => {
    if (open) {
      debouncedFetch(search)
    }
  }, [search, open, debouncedFetch])

  // 打开时加载数据
  useEffect(() => {
    if (open) {
      fetchBiographies({ page: 1, page_size: 20 })
      setSearch('')
    }
  }, [open])

  // 选择传记后导航
  const handleSelect = (biography: Biography) => {
    onOpenChange(false)
    navigate(`/biography/${biography.id}`, {
      state: {
        initialTitle: biography.title,
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/30 backdrop-blur-1" />
        <DialogContent
          className="w-448px h-auto h-[420px] p-0 overflow-hidden"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">搜索作品</DialogTitle>
          <DialogDescription className="sr-only">
            搜索作品名称
          </DialogDescription>

          <CommandPrimitive
            className="flex flex-col h-full overflow-hidden"
            value={selectedValue}
            onValueChange={setSelectedValue}
            shouldFilter={false}
          >
            {/* 搜索输入框 */}
            <div className="flex items-center gap-3 px-4 h-12 bor-b bor-1 shrink-0 text-tertiary">
              <iconpark-icon
                name="outline-search"
                size="18"
              />
              <CommandPrimitive.Input
                value={search}
                onValueChange={setSearch}
                placeholder="作品名"
                className="flex-1 h-full text-body-lg text-primary bg-transparent outline-none placeholder:text-tertiary"
              />
              {
                isLoading && (
                  <Spinner />
                )
              }
            </div>

            {/* 搜索结果列表 */}
            <CommandPrimitive.List className="flex-1 overflow-auto px-2 py-3 relative">
              {/* 首次加载时显示居中的加载指示器 */}
              {isLoading && biographies.length === 0 && (
                <div className="flex items-center justify-center py-6 h-full">
                  <Spinner />
                </div>
              )}

              {/* 无结果提示 - 只在非加载状态且确实没有结果时显示 */}
              {!isLoading && biographies.length === 0 && (
                <CommandPrimitive.Empty className="py-6 text-center text-tertiary">
                  {search ? '未找到匹配的作品' : '暂无作品'}
                </CommandPrimitive.Empty>
              )}

              {/* 结果列表 - 保持显示，即使正在加载新数据 */}
              {biographies.length > 0 && (
                <CommandPrimitive.Group className="flex flex-col gap-0.5">
                  {biographies.map((biography) => (
                    <SearchResultItem
                      key={biography.id}
                      biography={biography}
                      isSelected={selectedValue.startsWith(`${biography.id}-`)}
                      onSelect={() => handleSelect(biography)}
                    />
                  ))}
                </CommandPrimitive.Group>
              )}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default GlobalSearch
